import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import evidenceAPI from '../services/evidenceAPI';
import {
  Shield, CheckCircle, XCircle, Clock, Lightbulb, ChevronDown,
  ChevronUp, BarChart3, Layers, Plus, X, Search, Filter,
  Loader2, RefreshCw, Paperclip, TrendingUp, Trash2, Edit2,
  Zap
} from 'lucide-react';
import AdminSecurityHelpButton from '../components/Admin/AdminSecurityHelpButton';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FrameworkMapping {
  framework: string;
  requirement_id: string;
  requirement_name: string;
}

interface Control {
  id: number;
  control_name: string;
  control_description: string;
  control_category: string;
  implementation_status: 'implemented' | 'partially_implemented' | 'not_implemented' | 'planned';
  implementation_notes: string;
  framework_mappings: FrameworkMapping[];
  evidence_item_ids: number[];
  requirements_satisfied_count: number;
  frameworks_covered_count: number;
  gap_analysis: Record<string, any>;
  owner: string;
  review_date: string | null;
  is_sample: boolean;
}

interface SmartInsights {
  multi_framework_controls_count: number;
  top_unimplemented: (Control & { impact_score: number })[];
  framework_coverage: Record<string, { total: number; implemented: number; partial: number }>;
  total_controls: number;
  implemented_count: number;
  partial_count: number;
  not_implemented_count: number;
  overall_compliance_pct: number;
  quick_wins: { id: number; control_name: string; requirements_satisfied_count: number; frameworks_covered_count: number }[];
  has_sample_data: boolean;
}

interface GapAnalysis {
  control_name: string;
  implementation_status: string;
  requirements_satisfied: number;
  frameworks_covered: number;
  overall_compliance_before: number;
  overall_compliance_after: number;
  overall_improvement: number;
  framework_impact: Record<string, {
    requirements_count: number;
    coverage_before_pct: number;
    coverage_after_pct: number;
    improvement: number;
    requirements: { id: string; name: string }[];
  }>;
  already_implemented: boolean;
}

interface EvidenceName {
  id: number;
  evidence_name: string;
  evidence_date: string;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  implemented: {
    label: 'Implemented',
    bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300',
    icon: <CheckCircle className="w-4 h-4 text-green-600" />,
  },
  partially_implemented: {
    label: 'Partial',
    bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300',
    icon: <Clock className="w-4 h-4 text-yellow-600" />,
  },
  not_implemented: {
    label: 'Not Implemented',
    bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200',
    icon: <XCircle className="w-4 h-4 text-red-500" />,
  },
  planned: {
    label: 'Planned',
    bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200',
    icon: <Clock className="w-4 h-4 text-blue-500" />,
  },
};

const FRAMEWORK_OPTIONS = [
  'OWASP Top 10 2021', 'OWASP API Top 10', 'ISO 27001:2022',
  'PCI DSS 4.0', 'SOC 2 Type II', 'NIST CSF 2.0', 'GDPR',
  'HIPAA', 'FedRAMP', 'CIS Controls v8'
];

function groupByFramework(mappings: FrameworkMapping[]): Record<string, FrameworkMapping[]> {
  return mappings.reduce((acc, m) => {
    if (!acc[m.framework]) acc[m.framework] = [];
    acc[m.framework].push(m);
    return acc;
  }, {} as Record<string, FrameworkMapping[]>);
}

function formatEvidenceDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// ─── Gap Analysis Panel ───────────────────────────────────────────────────────

const GapAnalysisPanel: React.FC<{ controlId: number }> = ({ controlId }) => {
  const [data, setData] = useState<GapAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/control-mapping/${controlId}/gap-analysis`);
      setData(res.data.data);
      setLoaded(true);
    } catch {
      toast.error('Failed to load gap analysis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [controlId]);

  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-amber-600 py-2">
      <Loader2 className="w-3 h-3 animate-spin" /> Calculating gap analysis...
    </div>
  );

  if (!data) return null;

  if (data.already_implemented) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 flex items-center gap-2">
        <CheckCircle className="w-4 h-4" />
        This control is fully implemented — no compliance gaps.
      </div>
    );
  }

  const hasImpact = data.overall_improvement > 0;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Lightbulb className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold text-amber-800">Gap Analysis — Dynamic</span>
      </div>

      {/* Overall compliance impact */}
      {hasImpact && (
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1.5 text-gray-600">
            <span className="font-medium">Overall compliance:</span>
            <span className="font-bold text-red-600">{data.overall_compliance_before}%</span>
            <TrendingUp className="w-3.5 h-3.5 text-green-500" />
            <span className="font-bold text-green-600">{data.overall_compliance_after}%</span>
            <span className="text-green-600 font-semibold">(+{data.overall_improvement}%)</span>
          </div>
        </div>
      )}

      {/* Per-framework impact */}
      {Object.keys(data.framework_impact).length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-amber-700 uppercase tracking-wider">Impact by Framework</p>
          {Object.entries(data.framework_impact).map(([fw, impact]) => (
            <div key={fw} className="bg-white border border-amber-100 rounded p-2.5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-gray-700">{fw}</span>
                {impact.improvement > 0 && (
                  <span className="text-xs text-green-600 font-medium">+{impact.improvement}% coverage</span>
                )}
              </div>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                  <div className="bg-red-300 h-1.5 rounded-full" style={{ width: `${impact.coverage_before_pct}%` }} />
                </div>
                <span className="text-xs text-gray-500 w-8 text-right">{impact.coverage_before_pct}%</span>
                <span className="text-xs text-gray-400">→</span>
                <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                  <div className="bg-green-400 h-1.5 rounded-full" style={{ width: `${impact.coverage_after_pct}%` }} />
                </div>
                <span className="text-xs text-gray-700 font-medium w-8 text-right">{impact.coverage_after_pct}%</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {impact.requirements.map((r, i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-mono">{r.id}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!hasImpact && (
        <p className="text-sm text-amber-700">Implementing this control satisfies {data.requirements_satisfied} requirements across {data.frameworks_covered} frameworks.</p>
      )}
    </div>
  );
};

// ─── Control Card ─────────────────────────────────────────────────────────────

const ControlCard: React.FC<{
  control: Control;
  evidenceNames: Map<number, EvidenceName>;
  onDelete: (id: number) => void;
  onEdit: (control: Control) => void;
  deleting: boolean;
}> = ({ control, evidenceNames, onDelete, onEdit, deleting }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[control.implementation_status] || STATUS_CONFIG.not_implemented;
  const grouped = groupByFramework(control.framework_mappings || []);
  const isUnimplemented = control.implementation_status !== 'implemented';

  const linkedEvidence: EvidenceName[] = (control.evidence_item_ids || [])
    .map(id => evidenceNames.get(id))
    .filter((e): e is EvidenceName => !!e);

  return (
    <div className={`border rounded-xl overflow-hidden bg-white transition-shadow hover:shadow-md ${cfg.border}`}>
      {/* Card header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${cfg.bg}`}>
              {cfg.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-semibold text-gray-900">{control.control_name}</h3>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                  {cfg.icon}{cfg.label}
                </span>
                {control.control_category && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {control.control_category}
                  </span>
                )}
                {control.is_sample && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full text-xs">Sample</span>
                )}
              </div>
              {control.control_description && (
                <p className="text-sm text-gray-600 line-clamp-2">{control.control_description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => onEdit(control)}
              className="p-1.5 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 rounded transition-colors"
              title="Edit control"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(control.id)}
              disabled={deleting}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
              title="Delete control"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setExpanded(v => !v)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <BarChart3 className="w-4 h-4 text-indigo-500" />
            <span className="font-semibold text-gray-900">{control.requirements_satisfied_count}</span>
            <span>Requirements</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <Layers className="w-4 h-4 text-purple-500" />
            <span className="font-semibold text-gray-900">{control.frameworks_covered_count}</span>
            <span>Standards</span>
          </div>
          {control.evidence_item_ids?.length > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <Paperclip className="w-4 h-4 text-blue-500" />
              <span className="font-semibold text-gray-900">{control.evidence_item_ids.length}</span>
              <span>Evidence</span>
            </div>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-5 space-y-4">

          {/* Framework breakdown */}
          {Object.keys(grouped).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-indigo-500" />
                Satisfies {control.requirements_satisfied_count} Requirements Across {control.frameworks_covered_count} Standards
              </h4>
              <div className="space-y-3">
                {Object.entries(grouped).map(([framework, reqs]) => (
                  <div key={framework} className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{framework}</div>
                    <div className="space-y-1">
                      {reqs.map((req, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="flex-shrink-0 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-mono">
                            {req.requirement_id}
                          </span>
                          <span className="text-gray-700">{req.requirement_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Evidence linking */}
          {control.evidence_item_ids?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-blue-500" />
                {linkedEvidence.length > 0
                  ? `${linkedEvidence.length} piece${linkedEvidence.length !== 1 ? 's' : ''} of evidence support this control`
                  : `${control.evidence_item_ids.length} evidence item${control.evidence_item_ids.length !== 1 ? 's' : ''} linked`}
              </h4>
              <div className="flex flex-wrap gap-2">
                {linkedEvidence.length > 0 ? linkedEvidence.map(ev => (
                  <span key={ev.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg text-xs">
                    <Paperclip className="w-3 h-3" />
                    {ev.evidence_name}
                    <span className="text-blue-500">({formatEvidenceDate(ev.evidence_date)})</span>
                  </span>
                )) : control.evidence_item_ids.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-mono">#{id}</span>
                ))}
              </div>
            </div>
          )}

          {/* Dynamic Gap Analysis */}
          {isUnimplemented && (
            <GapAnalysisPanel controlId={control.id} />
          )}

          {/* Implementation notes */}
          {control.implementation_notes && (
            <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg p-3">
              <span className="font-medium text-gray-700">Notes: </span>{control.implementation_notes}
            </div>
          )}

          {/* Owner + review date */}
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            {control.owner && <span><strong>Owner:</strong> {control.owner}</span>}
            {control.review_date && (
              <span><strong>Review Date:</strong> {new Date(control.review_date).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Control Form Modal (shared by Add + Edit) ────────────────────────────────

const ControlFormModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  availableEvidence: EvidenceName[];
  editControl?: Control | null;
}> = ({ isOpen, onClose, onSuccess, availableEvidence, editControl }) => {
  const isEdit = !!editControl;

  const [form, setForm] = useState({
    control_name: '',
    control_description: '',
    control_category: '',
    implementation_status: 'not_implemented',
    implementation_notes: '',
    owner: ''
  });
  const [mappings, setMappings] = useState<FrameworkMapping[]>([
    { framework: '', requirement_id: '', requirement_name: '' }
  ]);
  const [selectedEvidenceIds, setSelectedEvidenceIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (editControl) {
      setForm({
        control_name: editControl.control_name || '',
        control_description: editControl.control_description || '',
        control_category: editControl.control_category || '',
        implementation_status: editControl.implementation_status || 'not_implemented',
        implementation_notes: editControl.implementation_notes || '',
        owner: editControl.owner || ''
      });
      setMappings(
        editControl.framework_mappings?.length
          ? editControl.framework_mappings
          : [{ framework: '', requirement_id: '', requirement_name: '' }]
      );
      setSelectedEvidenceIds(editControl.evidence_item_ids || []);
    } else {
      setForm({ control_name: '', control_description: '', control_category: '', implementation_status: 'not_implemented', implementation_notes: '', owner: '' });
      setMappings([{ framework: '', requirement_id: '', requirement_name: '' }]);
      setSelectedEvidenceIds([]);
    }
  }, [editControl, isOpen]);

  if (!isOpen) return null;

  const addMapping = () => setMappings(m => [...m, { framework: '', requirement_id: '', requirement_name: '' }]);
  const removeMapping = (i: number) => setMappings(m => m.filter((_, idx) => idx !== i));
  const updateMapping = (i: number, field: keyof FrameworkMapping, value: string) => {
    setMappings(m => m.map((item, idx) => idx === i ? { ...item, [field]: value } : item));
  };
  const toggleEvidence = (id: number) => {
    setSelectedEvidenceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.control_name.trim()) { toast.error('Control name is required'); return; }
    setSaving(true);
    try {
      const validMappings = mappings.filter(m => m.framework && m.requirement_name);
      const payload = { ...form, framework_mappings: validMappings, evidence_item_ids: selectedEvidenceIds };
      if (isEdit) {
        await api.put(`/api/control-mapping/${editControl!.id}`, payload);
        toast.success('Control updated');
      } else {
        await api.post('/api/control-mapping', payload);
        toast.success('Control added');
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'add'} control`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Control Mapping' : 'Add Control Mapping'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Control Name *</label>
            <input
              value={form.control_name}
              onChange={e => setForm(f => ({ ...f, control_name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
              placeholder="e.g. Multi-Factor Authentication"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.control_description}
              onChange={e => setForm(f => ({ ...f, control_description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
              placeholder="Describe what this control does..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input
                value={form.control_category}
                onChange={e => setForm(f => ({ ...f, control_category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                placeholder="e.g. Access Control"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Implementation Status</label>
              <select
                value={form.implementation_status}
                onChange={e => setForm(f => ({ ...f, implementation_status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
              >
                <option value="implemented">Implemented</option>
                <option value="partially_implemented">Partially Implemented</option>
                <option value="not_implemented">Not Implemented</option>
                <option value="planned">Planned</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
              <input
                value={form.owner}
                onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                placeholder="security@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Implementation Notes</label>
              <input
                value={form.implementation_notes}
                onChange={e => setForm(f => ({ ...f, implementation_notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                placeholder="Short note..."
              />
            </div>
          </div>

          {/* Framework mappings */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Framework Mappings</label>
              <button type="button" onClick={addMapping} className="text-xs text-[#221551] hover:text-[#454F83] font-medium flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add Row
              </button>
            </div>
            <div className="space-y-2">
              {mappings.map((m, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <select
                    value={m.framework}
                    onChange={e => updateMapping(i, 'framework', e.target.value)}
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#57C8F4]"
                  >
                    <option value="">Select framework…</option>
                    {FRAMEWORK_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <input
                    value={m.requirement_id}
                    onChange={e => updateMapping(i, 'requirement_id', e.target.value)}
                    className="w-20 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#57C8F4]"
                    placeholder="ID e.g. A07"
                  />
                  <input
                    value={m.requirement_name}
                    onChange={e => updateMapping(i, 'requirement_name', e.target.value)}
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#57C8F4]"
                    placeholder="Requirement name"
                  />
                  {mappings.length > 1 && (
                    <button type="button" onClick={() => removeMapping(i)} className="text-gray-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Evidence linking */}
          {availableEvidence.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Paperclip className="w-3 h-3 inline mr-1" />
                Link Evidence ({selectedEvidenceIds.length} selected)
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {availableEvidence.map(ev => (
                  <label key={ev.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedEvidenceIds.includes(ev.id)}
                      onChange={() => toggleEvidence(ev.id)}
                      className="rounded border-gray-300 text-[#221551] focus:ring-[#57C8F4]"
                    />
                    <span className="text-sm text-gray-700 flex-1 min-w-0 truncate">{ev.evidence_name}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{formatEvidenceDate(ev.evidence_date)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#221551] hover:bg-[#454F83] rounded-lg disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? <Edit2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {isEdit ? 'Save Changes' : 'Add Control'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ControlMapping: React.FC = () => {
  const [controls, setControls] = useState<Control[]>([]);
  const [insights, setInsights] = useState<SmartInsights | null>(null);
  const [evidenceNames, setEvidenceNames] = useState<Map<number, EvidenceName>>(new Map());
  const [availableEvidence, setAvailableEvidence] = useState<EvidenceName[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingControl, setEditingControl] = useState<Control | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const loadEvidenceNames = useCallback(async () => {
    try {
      const items = await evidenceAPI.getEvidenceItems();
      const map = new Map<number, EvidenceName>();
      const list: EvidenceName[] = [];
      items.forEach(item => {
        const ev: EvidenceName = { id: item.id, evidence_name: item.evidence_name, evidence_date: item.evidence_date };
        map.set(item.id, ev);
        list.push(ev);
      });
      setEvidenceNames(map);
      setAvailableEvidence(list);
    } catch {
      // non-critical
    }
  }, []);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('implementation_status', statusFilter);
      if (search) params.append('search', search);

      const [controlsRes, insightsRes] = await Promise.all([
        api.get(`/api/control-mapping?${params.toString()}`),
        api.get('/api/control-mapping/insights/smart')
      ]);

      setControls(controlsRes.data.data || []);
      setInsights(insightsRes.data.data);
    } catch {
      if (!silent) toast.error('Failed to load control mapping data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    loadData();
    loadEvidenceNames();
  }, [loadData, loadEvidenceNames]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this control mapping?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/api/control-mapping/${id}`);
      toast.success('Control deleted');
      await loadData(true);
    } catch {
      toast.error('Failed to delete control');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSeedData = async () => {
    setIsSeeding(true);
    try {
      const res = await api.post('/api/control-mapping/seed', {});
      toast.success(res.data.message);
      await loadData(true);
    } catch {
      toast.error('Failed to load sample data');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleClearSample = async () => {
    setIsClearing(true);
    try {
      const res = await api.delete('/api/control-mapping/clear-sample');
      toast.success(res.data.message);
      await loadData(true);
    } catch {
      toast.error('Failed to remove sample data');
    } finally {
      setIsClearing(false);
    }
  };

  const hasSampleData = insights?.has_sample_data ?? controls.some(c => c.is_sample);
  const topGap = insights?.top_unimplemented?.[0];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-indigo-600" />
              </div>
              Control Mapping & Reusability
            </h1>
            <p className="text-gray-600">See how your controls map across multiple compliance frameworks</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <AdminSecurityHelpButton screenId="control-mapping" />
            <button
              onClick={() => loadData(true)}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {!isLoading && (hasSampleData ? (
              <button
                onClick={handleClearSample}
                disabled={isClearing}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {isClearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                Hide Sample Data
              </button>
            ) : (
              <button
                onClick={handleSeedData}
                disabled={isSeeding}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#221551] hover:bg-[#454F83] rounded-lg transition-colors disabled:opacity-50"
              >
                {isSeeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Load Sample Data
              </button>
            ))}
            <button
              onClick={() => { setEditingControl(null); setIsModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
              style={{ background: '#57C8F4', color: '#221551' }}
            >
              <Plus className="w-4 h-4" />
              Add Control
            </button>
          </div>
        </div>
      </div>

      {/* Overall compliance score + quick wins */}
      {insights && insights.total_controls > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Compliance score */}
          <div className={`rounded-xl border p-5 ${
            insights.overall_compliance_pct >= 80 ? 'bg-green-50 border-green-200'
            : insights.overall_compliance_pct >= 50 ? 'bg-yellow-50 border-yellow-200'
            : 'bg-red-50 border-red-200'
          }`}>
            <p className="text-sm font-medium text-gray-600 mb-1">Overall Compliance Coverage</p>
            <p className={`text-5xl font-bold ${
              insights.overall_compliance_pct >= 80 ? 'text-green-600'
              : insights.overall_compliance_pct >= 50 ? 'text-yellow-600'
              : 'text-red-600'
            }`}>{insights.overall_compliance_pct}%</p>
            <p className="text-xs text-gray-500 mt-2">
              {insights.implemented_count} implemented · {insights.partial_count} partial · {insights.not_implemented_count} gaps
            </p>
          </div>

          {/* Quick wins */}
          {insights.quick_wins.length > 0 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-indigo-600" />
                <p className="text-sm font-semibold text-indigo-800">Quick Wins — Highest Impact Gaps</p>
              </div>
              <div className="space-y-2">
                {insights.quick_wins.map(qw => (
                  <div key={qw.id} className="flex items-center justify-between text-sm">
                    <span className="text-indigo-800 font-medium truncate flex-1 mr-2">{qw.control_name}</span>
                    <span className="text-indigo-600 flex-shrink-0 text-xs">
                      {qw.requirements_satisfied_count} reqs · {qw.frameworks_covered_count} frameworks
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Smart Insight Banner */}
      {insights && insights.multi_framework_controls_count > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-indigo-800">Smart Insight</p>
              <p className="text-sm text-indigo-700 mt-0.5">
                You have <strong>{insights.multi_framework_controls_count}</strong> controls that satisfy requirements across <strong>3+ frameworks simultaneously</strong>
                {topGap && (
                  <> — implementing <strong>"{topGap.control_name}"</strong> would satisfy <strong>{topGap.requirements_satisfied_count}</strong> additional requirements across {topGap.frameworks_covered_count} frameworks</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {insights && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Total Controls</p>
            <p className="text-3xl font-bold text-gray-900">{insights.total_controls}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Implemented</p>
            <p className="text-3xl font-bold text-green-600">{insights.implemented_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Multi-Framework</p>
            <p className="text-3xl font-bold text-indigo-600">{insights.multi_framework_controls_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Gaps</p>
            <p className="text-3xl font-bold text-red-500">{insights.not_implemented_count}</p>
          </div>
        </div>
      )}

      {/* Framework coverage */}
      {insights?.framework_coverage && Object.keys(insights.framework_coverage).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Layers className="w-4 h-4 text-purple-500" />
            Framework Coverage
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(insights.framework_coverage).map(([fw, data]) => {
              const pct = data.total > 0 ? Math.round(((data.implemented + data.partial * 0.5) / data.total) * 100) : 0;
              return (
                <div key={fw} className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-700 truncate mb-2" title={fw}>{fw}</p>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1">
                    <div
                      className={`h-1.5 rounded-full ${pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-400'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    {data.implemented}/{data.total} implemented ({pct}%)
                    {data.partial > 0 && <span className="text-yellow-600"> · {data.partial} partial</span>}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search controls..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
          >
            <option value="">All Statuses</option>
            <option value="implemented">Implemented</option>
            <option value="partially_implemented">Partially Implemented</option>
            <option value="not_implemented">Not Implemented</option>
            <option value="planned">Planned</option>
          </select>
        </div>
        {(statusFilter || search) && (
          <button onClick={() => { setStatusFilter(''); setSearch(''); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        <span className="ml-auto text-sm text-gray-500">{controls.length} control{controls.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Control list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#57C8F4]" />
        </div>
      ) : controls.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No controls found</p>
          <p className="text-sm text-gray-400 mt-1">
            {statusFilter || search ? 'Try clearing the filters' : 'Click "Load Sample Data" or "Add Control" to get started'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {controls.map(control => (
            <ControlCard
              key={control.id}
              control={control}
              evidenceNames={evidenceNames}
              onDelete={handleDelete}
              onEdit={c => { setEditingControl(c); setIsModalOpen(true); }}
              deleting={deletingId === control.id}
            />
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      <ControlFormModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingControl(null); }}
        onSuccess={() => loadData(true)}
        availableEvidence={availableEvidence}
        editControl={editingControl}
      />
    </div>
  );
};

export default ControlMapping;
